import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import './RedemptionHistory.css';

const RedemptionHistory = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, earned, redeemed
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('month'); // week, month, year, all

  const [stats, setStats] = useState({
    totalEarned: 0,
    totalRedeemed: 0,
    totalTransactions: 0,
    avgTransactionValue: 0,
    topRedemption: ''
  });

  useEffect(() => {
    fetchTransactions();
  }, [dateRange]);

  useEffect(() => {
    applyFilters();
  }, [transactions, filterType, searchTerm]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      const startDate = new Date();
      if (dateRange === 'week') {
        startDate.setDate(now.getDate() - 7);
      } else if (dateRange === 'month') {
        startDate.setMonth(now.getMonth() - 1);
      } else if (dateRange === 'year') {
        startDate.setFullYear(now.getFullYear() - 1);
      } else {
        startDate.setFullYear(2020, 0, 1); // All time
      }

      // Fetch loyalty transactions
      let transactionsQuery = query(
        collection(db, 'loyalty_transactions'),
        where('timestamp', '>=', startDate),
        orderBy('timestamp', 'desc')
      );

      const transactionsSnapshot = await getDocs(transactionsQuery);
      const transactionsData = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));

      setTransactions(transactionsData);

      // Calculate statistics
      const earned = transactionsData.filter(t => t.type === 'earned').reduce((sum, t) => sum + t.points, 0);
      const redeemed = transactionsData.filter(t => t.type === 'redeemed').reduce((sum, t) => sum + t.points, 0);
      const avgValue = transactionsData.length > 0
        ? transactionsData.reduce((sum, t) => sum + t.points, 0) / transactionsData.length
        : 0;

      // Find most common redemption reason
      const redemptions = transactionsData.filter(t => t.type === 'redeemed');
      const reasonCounts = {};
      redemptions.forEach(t => {
        reasonCounts[t.reason] = (reasonCounts[t.reason] || 0) + 1;
      });
      const topReason = Object.keys(reasonCounts).reduce((a, b) =>
        reasonCounts[a] > reasonCounts[b] ? a : b, ''
      );

      setStats({
        totalEarned: earned,
        totalRedeemed: redeemed,
        totalTransactions: transactionsData.length,
        avgTransactionValue: Math.round(avgValue),
        topRedemption: topReason || 'N/A'
      });

      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching transactions:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType);
    }

    // Filter by search term (customer name)
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Customer', 'Type', 'Points', 'Reason', 'Balance After'];
    const rows = filteredTransactions.map(t => [
      t.timestamp.toLocaleDateString(),
      t.customerName,
      t.type,
      t.points,
      t.reason,
      t.balanceAfter || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `redemption-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="redemption-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading transaction history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="redemption-container">
      <div className="redemption-header">
        <div>
          <h1>📊 Redemption History</h1>
          <p>Track all loyalty point transactions and redemptions</p>
        </div>
        <button className="btn-export" onClick={exportToCSV}>
          📥 Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card earned">
          <div className="stat-icon">✨</div>
          <div className="stat-info">
            <div className="stat-label">Total Points Earned</div>
            <div className="stat-value">{stats.totalEarned.toLocaleString()}</div>
          </div>
        </div>
        <div className="stat-card redeemed">
          <div className="stat-icon">💎</div>
          <div className="stat-info">
            <div className="stat-label">Total Points Redeemed</div>
            <div className="stat-value">{stats.totalRedeemed.toLocaleString()}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <div className="stat-label">Total Transactions</div>
            <div className="stat-value">{stats.totalTransactions}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-label">Avg Transaction Value</div>
            <div className="stat-value">{stats.avgTransactionValue}</div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-icon">🏆</div>
          <div className="stat-info">
            <div className="stat-label">Top Redemption</div>
            <div className="stat-value-small">{stats.topRedemption}</div>
          </div>
        </div>
        <div className="stat-card balance">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-label">Net Points</div>
            <div className="stat-value">{(stats.totalEarned - stats.totalRedeemed).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Date Range</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Transaction Type</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="earned">Earned Only</option>
            <option value="redeemed">Redeemed Only</option>
          </select>
        </div>

        <div className="filter-group search">
          <label>Search Customer</label>
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-info">
          Showing {filteredTransactions.length} of {transactions.length} transactions
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-section">
        {filteredTransactions.length === 0 ? (
          <div className="no-transactions">
            <div className="no-transactions-icon">📭</div>
            <h3>No transactions found</h3>
            <p>No loyalty transactions match your current filters</p>
          </div>
        ) : (
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Points</th>
                  <th>Reason</th>
                  <th>Balance After</th>
                  <th>Transaction ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(transaction => (
                  <tr key={transaction.id} className={transaction.type}>
                    <td>
                      <div className="transaction-date">
                        <div>{transaction.timestamp.toLocaleDateString()}</div>
                        <div className="time">{transaction.timestamp.toLocaleTimeString()}</div>
                      </div>
                    </td>
                    <td>
                      <div className="customer-cell">
                        <strong>{transaction.customerName}</strong>
                        <span className="customer-id">{transaction.customerId?.substring(0, 8)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${transaction.type}`}>
                        {transaction.type === 'earned' ? '✨ Earned' : '💎 Redeemed'}
                      </span>
                    </td>
                    <td>
                      <span className={`points-value ${transaction.type}`}>
                        {transaction.type === 'earned' ? '+' : '-'}{transaction.points}
                      </span>
                    </td>
                    <td>
                      <div className="reason-cell">{transaction.reason}</div>
                    </td>
                    <td>
                      <strong>{transaction.balanceAfter || 'N/A'}</strong>
                    </td>
                    <td>
                      <code className="transaction-id">{transaction.id.substring(0, 12)}...</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Insights */}
      <div className="insights-section">
        <h3>📈 Transaction Insights</h3>
        <div className="insights-grid">
          {stats.totalEarned > stats.totalRedeemed && (
            <div className="insight-card success">
              <span className="insight-icon">✅</span>
              <p>
                Customers are earning more points ({stats.totalEarned}) than redeeming ({stats.totalRedeemed}).
                This indicates good engagement and room for more redemption promotions.
              </p>
            </div>
          )}
          {stats.totalRedeemed > stats.totalEarned * 0.8 && (
            <div className="insight-card warning">
              <span className="insight-icon">⚠️</span>
              <p>
                High redemption rate detected ({((stats.totalRedeemed / stats.totalEarned) * 100).toFixed(0)}%).
                Consider reviewing point earning rules to maintain program sustainability.
              </p>
            </div>
          )}
          {stats.avgTransactionValue < 50 && stats.totalTransactions > 100 && (
            <div className="insight-card info">
              <span className="insight-icon">💡</span>
              <p>
                Average transaction value is {stats.avgTransactionValue} points.
                Consider offering bonus points for larger orders to increase engagement.
              </p>
            </div>
          )}
          {filterType === 'redeemed' && filteredTransactions.length > 0 && (
            <div className="insight-card info">
              <span className="insight-icon">🎯</span>
              <p>
                Most popular redemption: <strong>{stats.topRedemption}</strong>.
                This reward is driving the most engagement.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity Timeline */}
      <div className="timeline-section">
        <h3>🕐 Recent Activity</h3>
        <div className="timeline">
          {filteredTransactions.slice(0, 10).map(transaction => (
            <div key={transaction.id} className={`timeline-item ${transaction.type}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <strong>{transaction.customerName}</strong>
                  <span className="timeline-time">
                    {transaction.timestamp.toLocaleTimeString()} - {transaction.timestamp.toLocaleDateString()}
                  </span>
                </div>
                <div className="timeline-body">
                  {transaction.type === 'earned' ? (
                    <span className="timeline-action earned">
                      Earned {transaction.points} points
                    </span>
                  ) : (
                    <span className="timeline-action redeemed">
                      Redeemed {transaction.points} points
                    </span>
                  )}
                  <span className="timeline-reason">• {transaction.reason}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RedemptionHistory;
